import * as React from "react"

import { cn } from "@/lib/utils"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// ---------------------------------------------------------------------------
// SearchableSelect — array-driven variant of the shadcn Select. Meant to be a
// straightforward stand-in for native `<select>` elements so we can replace
// them across the app without hand-writing SelectTrigger/SelectItem markup for
// every filter dropdown.
//
// Usage:
//   <SearchableSelect
//     value={status}
//     onChange={setStatus}
//     placeholder="All statuses"
//     options={[
//       { value: "", label: "All" },
//       { value: "open", label: "Open" },
//       { value: "closed", label: "Closed" },
//     ]}
//   />
//
// Options may also be a plain array of strings — they'll be used for both
// value and label.
// ---------------------------------------------------------------------------

// cmdk / our SelectItem cannot use an empty string as a value. We treat "" as
// an "unset" sentinel and swap in this constant internally.
const EMPTY_SENTINEL = "__all__"

function normalizeOptions(options = []) {
  return options.map((opt) => {
    if (opt == null) return null
    if (typeof opt === "string" || typeof opt === "number") {
      return { value: String(opt), label: String(opt) }
    }
    return {
      value: opt.value == null ? "" : String(opt.value),
      label: opt.label ?? opt.name ?? String(opt.value ?? ""),
      disabled: !!opt.disabled,
    }
  }).filter(Boolean)
}

export const SearchableSelect = React.forwardRef(function SearchableSelect(
  {
    value,
    onChange,
    onValueChange,
    options,
    placeholder = "Select...",
    disabled,
    className,
    triggerClassName,
    contentClassName,
    searchable,
    emptyMessage,
    testId,
    id,
    name,
    "data-testid": dataTestId,
    ...rest
  },
  ref
) {
  const normalized = React.useMemo(() => normalizeOptions(options), [options])

  // Map "" -> sentinel and back so the underlying cmdk-based Select is happy.
  const toInner = React.useCallback(
    (v) => (v === "" || v === undefined || v === null ? EMPTY_SENTINEL : String(v)),
    []
  )
  const fromInner = React.useCallback(
    (v) => (v === EMPTY_SENTINEL ? "" : v),
    []
  )

  const handleChange = React.useCallback(
    (next) => {
      const outer = fromInner(next)
      onChange?.(outer)
      onValueChange?.(outer)
    },
    [onChange, onValueChange, fromInner]
  )

  const testIdAttr = dataTestId ?? testId

  return (
    <Select
      value={toInner(value)}
      onValueChange={handleChange}
      disabled={disabled}
      {...rest}
    >
      <SelectTrigger
        ref={ref}
        id={id}
        name={name}
        data-testid={testIdAttr}
        className={cn(triggerClassName, className)}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        searchable={searchable}
        emptyMessage={emptyMessage}
        className={contentClassName}
      >
        {normalized.map((opt) => (
          <SelectItem
            key={opt.value === "" ? EMPTY_SENTINEL : opt.value}
            value={opt.value === "" ? EMPTY_SENTINEL : opt.value}
            disabled={opt.disabled}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
})

export default SearchableSelect
